/*
 * The person's own work in every org (`GET /me/work`; docs/api.md, "Your work in every org"): what they started that
 * runs, or finished within the tray's ten minutes, each with its org. The task tray reads the open org's work as
 * before and adds this for the other orgs, so updates started from one account's Updates page stay in the header
 * while the person looks at another (Ryan, Nov 2). Read again when their work elsewhere changes (a `work` event, a
 * `bulk` event from another org; api/live.ts), when they start something, when the org changes, every 3 seconds while
 * something of theirs runs elsewhere, else every minute and when the window comes back.
 */
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useRef } from "react";
import { useOrg } from "../app/org";
import { onRunStarted, runFinished, runFrom, type BulkRun } from "./bulk";
import { api } from "./client";
import { keys } from "./keys";
import { onWatchStart } from "./live";
import { BUSY_STATES, type Move } from "./moves";
import { isActiveOp, type Operation } from "./types";

export type WorkOrg = { id: string; slug: string; name: string };
/** What a row's "where" line names ("Whitehardt · Acme · Live"): that org's own lists aren't read here. */
export type WorkPlace = { site: string | null; environment: string | null; server: string | null };
export type WorkOp = Operation & { org: WorkOrg; where: WorkPlace };
export type WorkRun = BulkRun & { org: WorkOrg };
export type WorkMove = Move & { org: WorkOrg; siteName: string | null };
export type Work = { operations: WorkOp[]; runs: WorkRun[]; moves: WorkMove[] };

const NONE: Work = { operations: [], runs: [], moves: [] };

function workFrom(d: { operations?: WorkOp[]; runs?: Record<string, unknown>[]; moves?: WorkMove[] }): Work {
  return {
    operations: d.operations ?? [],
    runs: (d.runs ?? []).flatMap((r) => {
      const run = runFrom(r);
      return run && r.org ? [{ ...run, org: r.org as WorkOrg }] : [];
    }),
    moves: d.moves ?? [],
  };
}

/** Whether something of the person's runs in an org other than `orgId`. */
function busyElsewhere(w: Work | undefined, orgId: string): boolean {
  if (!w) return false;
  return (
    w.operations.some((o) => o.org.id !== orgId && (isActiveOp(o.status) || o.followUp?.status === "waiting")) ||
    w.runs.some((b) => b.org.id !== orgId && !runFinished(b)) ||
    w.moves.some((m) => m.org.id !== orgId && BUSY_STATES.includes(m.state))
  );
}

/** The person's work in every org but the open one, with its org. */
export function useWorkElsewhere(): Work {
  const org = useOrg();
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: keys.myWork,
    queryFn: async ({ signal }) => workFrom(await api.get<Parameters<typeof workFrom>[0]>("/me/work", signal)),
    // Stale after 2 seconds, so coming back to the window reads it again.
    staleTime: 2_000,
    refetchInterval: (query) => (busyElsewhere(query.state.data, org.id) ? 3_000 : 60_000),
  });
  // Something started here is read at once, so it's there to show when the person opens another org.
  useEffect(() => {
    const refresh = () => void qc.invalidateQueries({ queryKey: keys.myWork });
    const offRuns = onRunStarted(refresh);
    const offOps = onWatchStart(refresh);
    return () => {
      offRuns();
      offOps();
    };
  }, [qc]);
  // Another org open: what's elsewhere is another set, read now rather than at the next poll.
  const shown = useRef(org.id);
  useEffect(() => {
    if (shown.current === org.id) return;
    shown.current = org.id;
    void qc.invalidateQueries({ queryKey: keys.myWork });
  }, [org.id, qc]);
  return useMemo(() => {
    const w = q.data ?? NONE;
    const away = <T extends { org: WorkOrg }>(xs: T[]) => xs.filter((x) => x.org.id !== org.id);
    return { operations: away(w.operations), runs: away(w.runs), moves: away(w.moves) };
  }, [q.data, org.id]);
}
